/**
 * Category Setup Hooks - Dataverse Integration
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { ICommonQueryParams } from "@/types";

import {
    createCategorySetup,
    deleteCategorySetup,
    getCategorySetupList,
    updateCategorySetup,
} from "../api/categorySetup";
import { queryKey } from "../constants";
import { ICategoryForm } from "../types/categorySetup";

/**
 * Hook to fetch category list with pagination
 */
export const useCategoryList = (queryParams?: ICommonQueryParams) => {
  return useQuery({
    queryKey: [queryKey.getCategorySetupList, queryParams],
    queryFn: () => getCategorySetupList(queryParams),
  });
};

/**
 * Hook to create a new category
 */
export const useCreateCategory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: ICategoryForm) => createCategorySetup(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey.getCategorySetupList] });
      toast.success("Category created successfully");
    },
  });
};

/**
 * Hook to update an existing category
 */
export const useUpdateCategory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<ICategoryForm> }) =>
      updateCategorySetup({ id, payload }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey.getCategorySetupList] });
      toast.success("Category updated successfully");
    },
  });
};

/**
 * Hook to delete a category
 */
export const useDeleteCategory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteCategorySetup(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey.getCategorySetupList] });
      toast.success("Category deleted successfully");
    },
  });
};
